<script lang="ts">
	import Icon from "@components/common/Icon.svelte";
	import Media from "@components/common/Media.svelte";
	import { getTranslate } from "@utils/translate.js";
	import type { ClassValue } from "svelte/elements";
	import type { Competition } from "@core/schema/Competition.js";
	import translations from "./OrganizerCard.translations.json" with { type: "json" };

	const { competition, class: customClass }: { competition: Competition; class?: ClassValue } = $props();

	const translate = getTranslate(translations);
	const organizer = $derived(competition.organizer);
	const contactName = $derived(`${organizer.firstName} ${organizer.lastName}`);

	// Lucide icons (the FlyFetch icon set) inlined as raw SVG — decorative, so the
	// adjacent link text carries the meaning for assistive tech.
	const iconAttrs =
		'xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"';
	const userIcon = `<svg ${iconAttrs}><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>`;
	const phoneIcon = `<svg ${iconAttrs}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>`;
	const mailIcon = `<svg ${iconAttrs}><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>`;
	const globeIcon = `<svg ${iconAttrs}><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/></svg>`;
	const facebookIcon = `<svg ${iconAttrs}><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>`;

	const linkClass =
		"ease-snappy focus-visible:ring-secondary/40 -mx-1 inline-flex items-center gap-2 rounded px-1 py-0.5 transition-colors duration-150 hover:text-secondary focus-visible:outline-none focus-visible:ring-2";
</script>

<aside
	class={["border-ink-200 shadow-card rounded-lg border bg-white p-5 sm:p-6", customClass]}
	aria-label={translate("organizer")}
>
	<p class="eyebrow text-secondary mb-3">{translate("organizer")}</p>
	<div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-5">
		<div class="shrink-0">
			{#if organizer.photo}
				<div class="border-ink-100 h-16 w-16 overflow-hidden rounded-full border">
					<Media
						class="h-full w-full object-cover"
						wrapperClass="block h-full w-full"
						source={organizer.photo}
						width={128}
						height={128}
						alt={contactName}
					/>
				</div>
			{:else}
				<div class="bg-secondary-50 text-secondary flex h-16 w-16 items-center justify-center rounded-full">
					<Icon icon={userIcon} class="h-7 w-7" />
				</div>
			{/if}
		</div>

		<div class="flex min-w-0 flex-col gap-2">
			<p class="font-display text-ink-950 text-[1.35rem] font-bold uppercase leading-tight tracking-[-0.01em]">
				{contactName}
			</p>

			<ul class="text-ink-700 flex flex-col gap-1.5 text-sm">
				{#if organizer.phone}
					<li>
						<a href="tel:{organizer.phone}" class={linkClass}>
							<Icon icon={phoneIcon} class="text-ink-400 h-4 w-4" />
							<span class="tabular">{organizer.phone}</span>
						</a>
					</li>
				{/if}
				<li>
					<a href="mailto:{organizer.email}" class={linkClass}>
						<Icon icon={mailIcon} class="text-ink-400 h-4 w-4" />
						<span class="break-all">{organizer.email}</span>
					</a>
				</li>
				{#if organizer.fbEventUrl}
					<li>
						<a href={organizer.fbEventUrl} target="_blank" rel="noopener noreferrer" class={linkClass}>
							<Icon icon={facebookIcon} class="text-ink-400 h-4 w-4" />
							<span>{translate("facebook")}</span>
						</a>
					</li>
				{/if}
				{#if organizer.webUrl}
					<li>
						<a href={organizer.webUrl} target="_blank" rel="noopener noreferrer" class={linkClass}>
							<Icon icon={globeIcon} class="text-ink-400 h-4 w-4" />
							<span>{translate("web")}</span>
						</a>
					</li>
				{/if}
			</ul>

			{#if organizer.ico}
				<p class="text-ink-500 text-xs">
					{translate("ico")}: <span class="tabular text-ink-700">{organizer.ico}</span>
				</p>
			{/if}
		</div>
	</div>
</aside>
